import { defineStore } from 'pinia'

import type { SiteNotificationRegistryReference } from '~/types/siteNotification'

export const SITE_NOTIFICATION_SESSION_VERSION = 1 as const
export const SITE_NOTIFICATION_STORAGE_PREFIX = 'wpn:site-notification'

export interface SiteNotificationSessionState {
  version: typeof SITE_NOTIFICATION_SESSION_VERSION
  dismissedIds: string[]
  claimedBlockingId: string | null
  claimedModalId: string | null
  nonBlockingCollapsed: boolean
}

interface SiteNotificationStoreState {
  pageRoutePath: string | null
  pageCandidates: SiteNotificationRegistryReference[]
  pageRegistrationId: number
  activeAccountId: string | null
  activeStorageKey: string | null
  hydrated: boolean
  session: SiteNotificationSessionState
}

const emptySession = (): SiteNotificationSessionState => ({
  version: SITE_NOTIFICATION_SESSION_VERSION,
  dismissedIds: [],
  claimedBlockingId: null,
  claimedModalId: null,
  nonBlockingCollapsed: true,
})

const isClient = () => {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.sessionStorage !== 'undefined'
    )
  } catch {
    return false
  }
}

const normalizeAccountId = (accountId: string | null | undefined) => {
  const normalized = typeof accountId === 'string' ? accountId.trim() : ''
  return normalized || null
}

export const getSiteNotificationStorageKey = (accountId: string) =>
  `${SITE_NOTIFICATION_STORAGE_PREFIX}:${encodeURIComponent(accountId)}`

// The extra colon cannot collide with an encoded account ID.
export const SITE_NOTIFICATION_ANONYMOUS_STORAGE_KEY = `${SITE_NOTIFICATION_STORAGE_PREFIX}:anonymous:session`

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const isValidSession = (
  value: unknown,
): value is SiteNotificationSessionState => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }

  const record = value as Record<string, unknown>
  return (
    record.version === SITE_NOTIFICATION_SESSION_VERSION &&
    Array.isArray(record.dismissedIds) &&
    record.dismissedIds.every(isNonEmptyString) &&
    (record.claimedBlockingId === null ||
      isNonEmptyString(record.claimedBlockingId)) &&
    (record.claimedModalId === null ||
      isNonEmptyString(record.claimedModalId)) &&
    typeof record.nonBlockingCollapsed === 'boolean'
  )
}

const copySession = (
  value: SiteNotificationSessionState,
): SiteNotificationSessionState => ({
  version: SITE_NOTIFICATION_SESSION_VERSION,
  dismissedIds: [...value.dismissedIds],
  claimedBlockingId: value.claimedBlockingId,
  claimedModalId: value.claimedModalId,
  nonBlockingCollapsed: value.nonBlockingCollapsed,
})

const removeStoredSession = (key: string) => {
  try {
    window.sessionStorage.removeItem(key)
  } catch (error) {
    console.warn('[site-notification] failed to remove session state', error)
  }
}

export const useSiteNotificationStore = defineStore('siteNotification', {
  state: (): SiteNotificationStoreState => ({
    pageRoutePath: null,
    pageCandidates: [],
    pageRegistrationId: 0,
    activeAccountId: null,
    activeStorageKey: null,
    hydrated: false,
    session: emptySession(),
  }),

  getters: {
    dismissedIds: (state) => state.session.dismissedIds,
    claimedBlockingId: (state) => state.session.claimedBlockingId,
    claimedModalId: (state) => state.session.claimedModalId,
    nonBlockingCollapsed: (state) => state.session.nonBlockingCollapsed,
    pageCandidatesForRoute:
      (state) => (routePath: string | null | undefined) =>
        routePath && routePath === state.pageRoutePath
          ? state.pageCandidates
          : [],
    isDismissed: (state) => (entryId: string) =>
      state.session.dismissedIds.includes(entryId),
  },

  actions: {
    registerPageCandidates(
      routePath: string,
      candidates: readonly SiteNotificationRegistryReference[],
    ) {
      this.pageRegistrationId += 1
      this.pageRoutePath = routePath
      this.pageCandidates = [...candidates]
      return this.pageRegistrationId
    },

    clearPageCandidates(routePath: string, registrationId?: number) {
      // A delayed cleanup from an older route must not erase a newer source.
      if (
        this.pageRoutePath !== routePath ||
        (registrationId !== undefined &&
          registrationId !== this.pageRegistrationId)
      )
        return
      this.pageRoutePath = null
      this.pageCandidates = []
    },

    hydrate(accountId: string | null | undefined, anonymous = false) {
      const nextAccountId = normalizeAccountId(accountId)
      const key = anonymous
        ? SITE_NOTIFICATION_ANONYMOUS_STORAGE_KEY
        : nextAccountId
          ? getSiteNotificationStorageKey(nextAccountId)
          : null

      if (!isClient() || !key) {
        this.activeAccountId = null
        this.activeStorageKey = null
        this.hydrated = false
        this.session = emptySession()
        return
      }

      if (this.activeStorageKey === key && this.hydrated) return

      // Never carry presentation state from the previous account in memory.
      this.activeAccountId = anonymous ? null : nextAccountId
      this.activeStorageKey = key
      this.hydrated = false
      this.session = emptySession()

      let stored: string | null = null
      try {
        stored = window.sessionStorage.getItem(key)
      } catch (error) {
        console.warn('[site-notification] failed to read session state', error)
      }

      if (stored !== null) {
        try {
          const parsed: unknown = JSON.parse(stored)
          if (isValidSession(parsed)) {
            this.session = copySession(parsed)
          } else {
            removeStoredSession(key)
          }
        } catch (error) {
          console.warn(
            '[site-notification] reset malformed session state',
            error,
          )
          removeStoredSession(key)
        }
      }

      this.hydrated = true
    },

    persist() {
      if (!isClient() || !this.activeStorageKey || !this.hydrated) return false

      try {
        window.sessionStorage.setItem(
          this.activeStorageKey,
          JSON.stringify(copySession(this.session)),
        )
        return true
      } catch (error) {
        console.error(
          '[site-notification] failed to persist session state',
          error,
        )
        return false
      }
    },

    claimBlocking(entryId: string) {
      if (!this.hydrated || !isNonEmptyString(entryId)) return false
      if (this.session.claimedBlockingId) {
        return this.session.claimedBlockingId === entryId
      }
      this.session.claimedBlockingId = entryId
      this.persist()
      return true
    },

    claimModal(entryId: string) {
      if (!this.hydrated || !isNonEmptyString(entryId)) return false
      if (this.session.claimedModalId) {
        return this.session.claimedModalId === entryId
      }
      this.session.claimedModalId = entryId
      this.persist()
      return true
    },

    dismiss(entryId: string) {
      if (!this.hydrated || !isNonEmptyString(entryId)) return false
      if (this.session.dismissedIds.includes(entryId)) return false
      this.session.dismissedIds.push(entryId)
      this.persist()
      return true
    },

    setNonBlockingCollapsed(collapsed: boolean) {
      if (!this.hydrated || typeof collapsed !== 'boolean') return false
      if (this.session.nonBlockingCollapsed === collapsed) return false
      this.session.nonBlockingCollapsed = collapsed
      this.persist()
      return true
    },

    toggleNonBlockingCollapsed() {
      if (!this.hydrated) return false
      this.session.nonBlockingCollapsed = !this.session.nonBlockingCollapsed
      this.persist()
      return this.session.nonBlockingCollapsed
    },

    clearOnLogout() {
      if (isClient() && this.activeStorageKey) {
        try {
          window.sessionStorage.removeItem(this.activeStorageKey)
        } catch (error) {
          console.warn(
            '[site-notification] failed to clear session state',
            error,
          )
        }
      }

      this.activeAccountId = null
      this.activeStorageKey = null
      this.hydrated = false
      this.session = emptySession()
    },
  },
})
